import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { API_URL } from '../../config/api';
import PresignedAvatar from '../common/PresignedAvatar';
import CompactRichEditor, { collectMentionedUserIdsFromPlain } from '../common/CompactRichEditor';
import MessageMentionContent from '../common/MessageMentionContent';
import ChatMessageBubblePreview from '../chat/ChatMessageBubblePreview';
import AttachmentGroupBar from './AttachmentGroupBar';
import ImageViewerModal from './ImageViewerModal';
import WorkflowAssigneeDropdown from './WorkflowAssigneeDropdown';
import WorkflowTrackDialog from './WorkflowTrackDialog';
import ReactionPicker from './ReactionPicker';
import MessageReactionBadge from './MessageReactionBadge';
import {
  addMessageCustomTag,
  removeMessageCustomTag,
  assignWorkflowMessage,
  updateWorkflowTrack,
  sendMessage,
  addMessage,
  removeMessage,
  addReaction,
} from '../../store/slices/chatSlice';
import { addNotification } from '../../store/slices/uiSlice';
import PostCompletionEditor from '../templates/PostCompletionEditor';
import WorkCloseProgressOverlay from '../common/WorkCloseProgressOverlay';
import WorkItemHistoryPanel from '../common/WorkItemHistoryPanel';
import WorkItemShareMenu from '../common/WorkItemShareMenu';
import { createTask, fetchTasks, fetchTasksByMapTaskId, TaskStatus, updateTask } from '../../store/slices/taskSlice';
import SpacePicker from '../tasks/SpacePicker';
import {
  WORKFLOW_CLOSED_STATUS_ID,
  WORK_ITEM_TYPE_TASK_ID,
  WORKFLOW_URGENT_ID,
  partitionMessageReactions,
  getActiveWorkflowByCategory,
  getReactionDisplay,
  applyOptimisticReaction,
  isWorkflowReaction,
  resolveUrgentWorkflowReactionId,
  messageHasUrgentWorkflow,
  mergeReactionsWithWorkflowCategory,
} from '../../utils/reactionConfig';
import { WORK_ITEM_SHARE_KIND } from '../../utils/workItemShare';
import { useUrgentDeadlineDialog } from './UrgentDeadlineDialog';
import UrgentCountdownBadge from './UrgentCountdownBadge';
import { collectLocalChannelMessages, resolveLocalMessage } from '../../realm/offlineLookups';
import { upsertRealmDocs } from '../../realm';
import { formatEstimateTime } from '../../utils/estimateTime';
import { ClockIcon } from './SidebarIcons';
import QuickTaskComposer from '../tasks/QuickTaskComposer';
import TaskSubtaskItem from '../tasks/TaskSubtaskItem';
import TaskViewDialog from '../tasks/TaskViewDialog';
import EnableLiveTrackingButton from '../liveTracking/EnableLiveTrackingButton';
import {
  isWorkflowMappingParent,
  isMappedChat,
  unfinishedWorkflowLinkedCount,
  visibleWorkflowLinkedTasks,
  resolveWorkflowCloseMinutes,
  uniqueTasksById,
  formatTaskAssigneeLabel,
  loadMappedByTaskId,
  apiErrorMessage,
} from '../../utils/workflowTaskUtils';
import {
  htmlIsMeaningful,
  mergeComments,
  commentKey,
  collectThreadReplies,
  removeCommentById,
} from '../../utils/threadCommentUtils';
import { downloadAttachment, openAttachmentFallback, readBlobDurationSeconds } from '../../utils/fileUtils';
import ConfirmModal from '../common/ConfirmModal';
import {
  VoiceNoteBanner,
  VoiceNoteMicButton,
  VoiceNoteSendModal,
  blobToVoiceFile,
  captionToHtml,
  useVoiceRecorder,
} from './VoiceNoteRecorder';
import { uploadFilesWithProgress, inferAttachmentFileType } from '../../utils/uploadFilesWithProgress';

const CHIP_TONE = {
  violet: 'border-violet-200/90 bg-violet-50 text-violet-800 dark:border-violet-700/60 dark:bg-violet-900/25 dark:text-violet-200',
  blue: 'border-sky-200/90 bg-sky-50 text-sky-800 dark:border-sky-700/60 dark:bg-sky-900/25 dark:text-sky-200',
  indigo: 'border-indigo-200/90 bg-indigo-50 text-indigo-800 dark:border-indigo-700/60 dark:bg-indigo-900/25 dark:text-indigo-200',
  cyan: 'border-cyan-200/90 bg-cyan-50 text-cyan-800 dark:border-cyan-700/60 dark:bg-cyan-900/25 dark:text-cyan-200',
  teal: 'border-teal-200/90 bg-teal-50 text-teal-800 dark:border-teal-700/60 dark:bg-teal-900/25 dark:text-teal-200',
  green: 'border-emerald-200/90 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-900/25 dark:text-emerald-200',
  amber: 'border-amber-200/90 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/25 dark:text-amber-100',
  red: 'border-rose-200/90 bg-rose-50 text-rose-800 dark:border-rose-700/60 dark:bg-rose-900/25 dark:text-rose-200',
  slate: 'border-gray-200/90 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-300',
};

const chipClass = (tone) =>
  `inline-flex items-center gap-0.5 max-w-[9rem] min-h-[1.375rem] px-1.5 py-0.5 rounded-md text-[10px] font-semibold leading-tight truncate border ${CHIP_TONE[tone] || CHIP_TONE.slate}`;

const resolveItemReactions = (message, item) => {
  const fromMessage = Array.isArray(message?.reactions) ? message.reactions : [];
  const fromItem = Array.isArray(item?.reactions) ? item.reactions : [];
  const list = fromMessage.length > 0 ? fromMessage : fromItem;
  const byCategory = message?.workflowByCategory || item?.workflowByCategory || {};
  if (list.length > 0) {
    return mergeReactionsWithWorkflowCategory(list, byCategory);
  }
  return mergeReactionsWithWorkflowCategory([], byCategory);
};

const PICKER_MAX_WIDTH = 360;
const PICKER_ESTIMATED_HEIGHT = 360;
const PICKER_GAP = 8;
const REACTION_PICKER_Z_INDEX = 1200;

const computeReactionPickerPosition = (anchorEl) => {
  if (!anchorEl) return null;
  const rect = anchorEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = rect.bottom + PICKER_GAP;
  let left = rect.left;
  if (left + PICKER_MAX_WIDTH > vw) left = Math.max(8, vw - PICKER_MAX_WIDTH - 8);
  if (left < 8) left = 8;
  if (top + PICKER_ESTIMATED_HEIGHT > vh) {
    top = rect.top - PICKER_ESTIMATED_HEIGHT - PICKER_GAP;
  }
  if (top < 8) top = 8;
  return { top, left };
};

const toErrorMessage = (err, fallback) => {
  const data = err?.response?.data ?? err?.data ?? err;
  if (typeof data === 'string' && data.trim()) return data.replace(/^Error:\s*/i, '');
  if (data && typeof data === 'object') {
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
    if (typeof data.error === 'string' && data.error.trim()) return data.error;
  }
  if (typeof err?.message === 'string' && err.message.trim()) return err.message;
  return fallback;
};

const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

const looksLikeHtml = (value) => {
  const v = String(value || '');
  return /<[a-z][\s\S]*?>/i.test(v) || /&lt;\/?[a-z]/i.test(v);
};

const normalizeDescriptionHtml = (value) => {
  let html = String(value || '');
  if (/&lt;\/?[a-z]/i.test(html)) {
    html = html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }
  return html;
};

const renderRichOrPlain = (value, emptyText) => {
  if (value && typeof value !== 'string') {
    return <p className="whitespace-pre-wrap break-words">{emptyText}</p>;
  }
  if (!value || !String(value).trim()) {
    return <p className="whitespace-pre-wrap break-words">{emptyText}</p>;
  }
  if (looksLikeHtml(value)) {
    return (
      <MessageMentionContent
        isRichText
        content={normalizeDescriptionHtml(value)}
        mentionOnLight
        className="ql-editor-content [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md"
        style={{ fontSize: '0.875rem', lineHeight: '1.7' }}
      />
    );
  }
  return (
    <MessageMentionContent
      content={value}
      mentionOnLight
      className="whitespace-pre-wrap break-words text-sm"
    />
  );
};

const userLabel = (user, currentUserId) => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
    || user?.username
    || 'Unknown user';
  if (currentUserId && String(user?.id) === String(currentUserId)) return `You (${name})`;
  return name;
};

const formatDaysAgo = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const start = (input) => {
    const next = new Date(input);
    next.setHours(0, 0, 0, 0);
    return next.getTime();
  };
  const diffDays = Math.round((start(new Date()) - start(date)) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
};

const truncatePlainText = (value, maxLen = 80) => {
  const trimmed = stripHtmlTags(value).trim();
  if (!trimmed) return '';
  return trimmed.length <= maxLen ? trimmed : `${trimmed.slice(0, maxLen - 1)}…`;
};

const WorkflowViewDialog = ({
  item,
  mappedTasks = [],
  mappedMessagesByTaskId = {},
  mappedByTaskId = {},
  onClose,
  onComplete,
  onUndoComplete,
  completing = false,
  onNavigateToChat,
  onViewImage,
  onDownload,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onCompleteTask,
  savingTask = false,
  workTimerDisabled = false,
  zIndexClass = 'z-[1000]',
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const currentUserId = currentUser?.id;
  const isAdminUser = currentUser?.role === 'ADMIN' || currentUser?.role === 'ORG_ADMIN';
  const { users = [] } = useSelector((state) => state.users);
  const { channels = [], directMessages = [] } = useSelector((state) => state.channels);
  const reduxChannelMessages = useSelector(
    (state) => (item?.channelId ? (state.chat.messages?.[item.channelId] || []) : []),
  );
  const reduxTasks = useSelector((state) => state.tasks?.tasks || []);
  const currentTask = useSelector((state) => state.tasks?.currentTask || null);
  const { promptUrgentDeadline, urgentDeadlineDialog } = useUrgentDeadlineDialog();

  const [liveMessage, setLiveMessage] = useState(null);
  const [viewingGallery, setViewingGallery] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [tagDraft, setTagDraft] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [addingTagOpen, setAddingTagOpen] = useState(false);
  const tagInputRef = useRef(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignQuery, setAssignQuery] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [popupTab, setPopupTab] = useState('details');
  const [showTrackDialog, setShowTrackDialog] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [reactionPickerPosition, setReactionPickerPosition] = useState(null);
  const reactionTriggerRef = useRef(null);
  const pendingReactionClicks = useRef(new Set());

  const [fetchedTasks, setFetchedTasks] = useState([]);
  const [savingLocalTask, setSavingLocalTask] = useState(false);
  const [viewingLinkedTask, setViewingLinkedTask] = useState(null);
  const [addingLinkedTask, setAddingLinkedTask] = useState(false);
  const [forceClosing, setForceClosing] = useState(false);
  const [forceCloseConfirmOpen, setForceCloseConfirmOpen] = useState(false);
  const [localClosing, setLocalClosing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const commentsEndRef = useRef(null);
  const commentsScrollRef = useRef(null);
  const commentQuillRef = useRef(null);
  const descriptionQuillRef = useRef(null);
  const shouldStickToLatestComment = useRef(true);
  const commentVoice = useVoiceRecorder({
    onPermissionError: (message) => {
      dispatch(addNotification({
        type: 'error',
        title: 'Voice note',
        message: message || 'Microphone access is required for captions.',
      }));
    },
  });
  const commentVoiceRef = useRef(commentVoice);
  commentVoiceRef.current = commentVoice;

  const channel = useMemo(
    () => [...channels, ...directMessages].find((ch) => ch.id === item?.channelId) || null,
    [channels, directMessages, item?.channelId],
  );

  const channelMemberIds = useMemo(() => {
    const memberIds = channel?.memberIds?.length
      ? [...channel.memberIds]
      : (users || []).map((u) => u.id);
    return memberIds.filter(Boolean);
  }, [channel?.memberIds, users]);

  const assignableUsers = useMemo(() => {
    const memberIds = channel?.memberIds?.length
      ? [...channel.memberIds]
      : (users || []).map((u) => u.id);
    if (item?.senderId && !memberIds.some((id) => String(id) === String(item.senderId))) {
      memberIds.unshift(item.senderId);
    }
    if (currentUserId && !memberIds.some((id) => String(id) === String(currentUserId))) {
      memberIds.unshift(currentUserId);
    }
    const seen = new Set();
    return memberIds
      .filter((id) => {
        const key = String(id);
        if (!id || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((id) => (users || []).find((u) => String(u.id) === String(id)))
      .filter((u) => u && u.isActive !== false)
      .map((u) => ({
        id: u.id,
        label: userLabel(u, currentUserId),
        isSelf: String(u.id) === String(currentUserId),
        profilePicture: u.profilePicture || '',
      }))
      .sort((a, b) => {
        if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
        return a.label.localeCompare(b.label);
      });
  }, [channel, users, currentUserId, item?.senderId]);

  const defaultTaskAssigneeId = useMemo(() => {
    const creatorId = item?.senderId;
    if (creatorId && assignableUsers.some((user) => String(user.id) === String(creatorId))) {
      return String(creatorId);
    }
    return currentUserId ? String(currentUserId) : '';
  }, [item?.senderId, assignableUsers, currentUserId]);

  const taskAssigneeLabel = (task) => formatTaskAssigneeLabel(
    task,
    [...assignableUsers, ...(users || [])],
    currentUserId,
  );

  useEffect(() => {
    if (!item?.messageId) return undefined;
    let cancelled = false;
    setLiveMessage(null);
    setComments([]);
    setEditingDescription(false);
    setEditingTitle(false);
    setAddingLinkedTask(false);
    setCommentDraft('');
    shouldStickToLatestComment.current = true;
    const load = async () => {
      const local = await resolveLocalMessage(item.messageId);
      if (!cancelled && local) setLiveMessage(local);

      let thread = [];
      try {
        thread = await collectLocalChannelMessages(item.channelId, reduxChannelMessages);
      } catch {
        thread = Array.isArray(reduxChannelMessages) ? reduxChannelMessages : [];
      }

      const applyThread = (msgs) => {
        if (cancelled) return;
        setComments((prev) => mergeComments(prev, collectThreadReplies(msgs, item.messageId)));
      };
      applyThread(thread);

      if (item.channelId) {
        try {
          const token = localStorage.getItem('token');
          const around = await axios.get(
            `${API_URL}/messages/channel/${item.channelId}/around/${item.messageId}`,
            {
              params: { before: 40, after: 40 },
              headers: { Authorization: token ? `Bearer ${token}` : '' },
            },
          );
          const aroundMsgs = around.data?.messages || [];
          const found = aroundMsgs.find((msg) => String(msg.id) === String(item.messageId));
          if (!cancelled && found) setLiveMessage(found);
          applyThread(aroundMsgs);
        } catch {
          // ignore
        }
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/messages/${item.messageId}`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        if (!cancelled && response.data && typeof response.data === 'object' && !response.data.error) {
          setLiveMessage(response.data);
        }
      } catch {
        // keep local / around message
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/messages/${item.messageId}/replies`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        if (!cancelled && Array.isArray(response.data)) {
          setComments((prev) => mergeComments(prev, response.data));
        }
      } catch {
        // keep local/around replies
      }
    };
    load();
    return () => { cancelled = true; };
  }, [item?.messageId, item?.channelId]);

  useEffect(() => {
    setPopupTab('details');
  }, [item?.messageId]);

  useEffect(() => {
    if (!item?.messageId) {
      setFetchedTasks([]);
      return undefined;
    }
    let cancelled = false;
    dispatch(fetchTasks({ messageId: item.messageId }))
      .unwrap()
      .then(async (list) => {
        const tasks = Array.isArray(list) ? list : [];
        if (!cancelled) setFetchedTasks(tasks);
        const parents = tasks.filter((task) => isWorkflowMappingParent(task, item.messageId));
        const childLists = await Promise.all(parents.map(async (parent) => {
          if (!parent?.id) return [];
          try {
            const children = await dispatch(fetchTasksByMapTaskId(parent.id)).unwrap();
            return Array.isArray(children) ? children : [];
          } catch {
            return [];
          }
        }));
        const children = childLists.flat();
        if (!cancelled && children.length) {
          setFetchedTasks((prev) => {
            const byId = new Map((prev || []).map((task) => [task.id, task]));
            children.forEach((task) => {
              if (task?.id && !byId.has(task.id)) byId.set(task.id, task);
            });
            return Array.from(byId.values());
          });
        }
      })
      .catch(() => {
        if (!cancelled) setFetchedTasks([]);
      });
    return () => { cancelled = true; };
  }, [dispatch, item?.messageId]);

  useEffect(() => {
    const onTaskUpdated = (event) => {
      const task = event?.detail?.task;
      if (!task?.id) return;
      setFetchedTasks((prev) => {
        const exists = (prev || []).some((itemTask) => itemTask?.id === task.id);
        if (!exists && !isWorkflowMappingParent(task, item?.messageId)) return prev;
        return uniqueTasksById(prev, task);
      });
      setViewingLinkedTask((prev) => (
        prev?.id === task.id ? { ...prev, ...task } : prev
      ));
    };
    window.addEventListener('workloop:task-updated', onTaskUpdated);
    return () => window.removeEventListener('workloop:task-updated', onTaskUpdated);
  }, [item?.messageId]);

  useLayoutEffect(() => {
    if (!showReactionPicker) {
      setReactionPickerPosition(null);
      return;
    }
    setReactionPickerPosition(computeReactionPickerPosition(reactionTriggerRef.current));
  }, [showReactionPicker]);

  useEffect(() => {
    if (!showReactionPicker) return undefined;
    const update = () => {
      setReactionPickerPosition(computeReactionPickerPosition(reactionTriggerRef.current));
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [showReactionPicker]);

  useEffect(() => {
    if (!showReactionPicker) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-reaction-picker]') || target.closest('[data-workflow-reaction-trigger]')) {
        return;
      }
      setShowReactionPicker(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setShowReactionPicker(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showReactionPicker]);

  useEffect(() => {
    if (!shouldStickToLatestComment.current) return;
    const container = commentsScrollRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      return;
    }
    commentsEndRef.current?.scrollIntoView({ block: 'end' });
  }, [comments.length]);

  const linkedTasks = useMemo(() => {
    const sourceTask = item?.sourceTask;
    return uniqueTasksById(
      sourceTask,
      mappedTasks,
      reduxTasks,
      currentTask,
      fetchedTasks,
    ).filter((task) => isWorkflowMappingParent(task, item?.messageId));
  }, [mappedTasks, fetchedTasks, reduxTasks, currentTask, item?.messageId, item?.sourceTask]);

  const mappedChats = useMemo(() => {
    const byId = new Map();
    [...(mappedTasks || []), ...(fetchedTasks || [])].forEach((task) => {
      if (isMappedChat(task) && task?.id && !byId.has(task.id)) {
        byId.set(task.id, task);
      }
    });
    return Array.from(byId.values());
  }, [mappedTasks, fetchedTasks]);

  const handleViewSourceImage = useCallback((images, index) => {
    if (onViewImage) {
      onViewImage(images, index);
      return;
    }
    setViewingGallery({
      images: Array.isArray(images) ? images : [],
      index: Number.isFinite(index) && index >= 0 ? index : 0,
    });
  }, [onViewImage]);

  const handleDownloadSource = useCallback(async (attachment) => {
    if (onDownload) {
      onDownload(attachment);
      return;
    }
    try {
      await downloadAttachment(attachment);
    } catch {
      openAttachmentFallback(attachment);
    }
  }, [onDownload]);

  if (!item) return null;

  const message = liveMessage || item;
  const chatContent = [message.content, item.contentHtml, item.content]
    .find((value) => typeof value === 'string' && value.trim()) || '';
  const savedTitle = (message.workflowTitle || item.workflowTitle || '').trim();
  const title = savedTitle || truncatePlainText(chatContent) || 'Workflow item';
  const description = [message.workflowDescription, item.workflowDescription, chatContent]
    .find((value) => typeof value === 'string' && value.trim()) || '';
  const sourceAttachments = Array.isArray(message.attachments) && message.attachments.length
    ? message.attachments
    : (Array.isArray(item.attachments) ? item.attachments : []);
  const sourceAttachmentGroupName = message.attachmentGroupName || item.attachmentGroupName || '';
  const raisedAgo = formatDaysAgo(item.createdAt || item.timestamp || message.createdAt || message.timestamp);
  const completionTimeLabel = formatEstimateTime(
    message.completionTimeInMinutes || item.completionTimeInMinutes,
  );
  const customTags = message.customTags || item.customTags || [];
  const assigneeId = message.workflowAssignedToUserId || item.workflowAssignedToUserId || null;
  const assigneeName = message.workflowAssignedToName || item.workflowAssignedToName || '';
  const trackId = (
    liveMessage
      ? (liveMessage.workflowTrackId || '')
      : (item.workflowTrackId || '')
  ).trim();
  const trackLink = (
    liveMessage
      ? (liveMessage.workflowTrackLink || '')
      : (item.workflowTrackLink || '')
  ).trim();
  const resolvedReactions = resolveItemReactions(message, item);
  const { workflow: workflowReactions, free: freeReactions } = partitionMessageReactions(resolvedReactions);
  const workflowByCategory = getActiveWorkflowByCategory(resolvedReactions);
  const isClosed = Object.values(workflowByCategory).includes(WORKFLOW_CLOSED_STATUS_ID);
  const typeId = workflowByCategory['work-item-type'];
  const typeDisplay = typeId ? getReactionDisplay(typeId) : null;
  const statusId = workflowByCategory['workflow-status'];
  const statusDisplay = statusId ? getReactionDisplay(statusId) : null;

  const currentUserName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ').trim()
    || currentUser?.username
    || 'You';

  const handleReactionClick = async (reactionId) => {
    const messageId = item.messageId;
    if (!messageId || !reactionId || !currentUserId) return;
    const mappedReactionId = resolveUrgentWorkflowReactionId(reactionId);
    const reactionKey = isWorkflowReaction(mappedReactionId) ? mappedReactionId.trim() : mappedReactionId;
    if (!reactionKey) return;

    if (reactionKey.startsWith('wf:work-item-type:')) {
      if (typeId && typeId === reactionKey) {
        dispatch(addNotification({
          type: 'error',
          message: 'Workflow type cannot be removed once set',
        }));
        setShowReactionPicker(false);
        return;
      }
      if (typeId && reactionKey === WORK_ITEM_TYPE_TASK_ID) {
        dispatch(addNotification({
          type: 'error',
          message: 'Cannot change a workflow to a task',
        }));
        setShowReactionPicker(false);
        return;
      }
      if (!typeId && reactionKey === WORK_ITEM_TYPE_TASK_ID) {
        dispatch(addNotification({
          type: 'error',
          message: 'Use Create Task from chat to create a task',
        }));
        setShowReactionPicker(false);
        return;
      }
    }

    let completionTimeInMinutes;
    if (reactionKey === WORKFLOW_CLOSED_STATUS_ID && !isClosed) {
      const mapped = (mappedByTaskId && Object.keys(mappedByTaskId).length > 0)
        ? mappedByTaskId
        : await loadMappedByTaskId(
          linkedTasks,
          (taskId) => dispatch(fetchTasksByMapTaskId(taskId)).unwrap(),
        );
      const { minutes, error } = await resolveWorkflowCloseMinutes({
        linkedTasks,
        mappedByTaskId: mapped,
        workflowTitle: title,
      });
      if (error) {
        dispatch(addNotification({ type: 'error', message: error }));
        return;
      }
      if (minutes == null) return;
      if (Number(minutes) > 0) {
        completionTimeInMinutes = minutes;
      }
    }

    let urgentDeadlineAt;
    if (reactionKey === WORKFLOW_URGENT_ID && !messageHasUrgentWorkflow(resolvedReactions)) {
      const deadline = await promptUrgentDeadline();
      if (!deadline) return;
      urgentDeadlineAt = deadline;
    }

    const pendingKey = `${messageId}:${reactionKey}`;
    if (pendingReactionClicks.current.has(pendingKey)) return;
    pendingReactionClicks.current.add(pendingKey);
    setShowReactionPicker(false);
    const isClosingWorkflow = reactionKey === WORKFLOW_CLOSED_STATUS_ID;
    if (isClosingWorkflow) setLocalClosing(true);
    const previousLive = liveMessage || item;
    const channelId = item.channelId
      || liveMessage?.channelId
      || reduxChannelMessages.find((msg) => String(msg.id) === String(messageId))?.channelId;
    const nextReactions = applyOptimisticReaction(resolveItemReactions(previousLive, item), reactionKey, currentUserId);
    setLiveMessage((prev) => {
      const base = prev || item;
      return {
        ...base,
        channelId: channelId || base.channelId,
        reactions: nextReactions,
        workflowByCategory: getActiveWorkflowByCategory(nextReactions),
        ...(completionTimeInMinutes ? { completionTimeInMinutes } : {}),
        ...(urgentDeadlineAt ? { urgentDeadlineAt } : {}),
      };
    });
    try {
      const updated = await dispatch(addReaction({
        messageId,
        emoji: reactionKey,
        channelId,
        userId: currentUserId,
        completionTimeInMinutes,
        urgentDeadlineAt,
      })).unwrap();
      if (updated && typeof updated === 'object') {
        const mergedReactions = mergeReactionsWithWorkflowCategory(
          updated.reactions || nextReactions,
          updated.workflowByCategory,
        );
        const mapped = {
          ...(previousLive || item),
          ...updated,
          reactions: mergedReactions,
          workflowByCategory: getActiveWorkflowByCategory(mergedReactions),
        };
        setLiveMessage(mapped);
        if (mapped.channelId) {
          dispatch(addMessage({
            channelId: mapped.channelId,
            message: mapped,
            currentUserId,
            skipUnreadCountIncrement: true,
          }));
        }
      }
      if (isWorkflowReaction(reactionKey)) {
        window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', {
          detail: reactionKey === WORKFLOW_CLOSED_STATUS_ID
            ? { messageId, action: 'completed', completionTimeInMinutes }
            : { messageId },
        }));
      }
      if (isClosingWorkflow) {
        await new Promise((resolve) => window.setTimeout(resolve, 900));
      }
    } catch (err) {
      setLiveMessage(previousLive);
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to update reaction'),
      }));
    } finally {
      pendingReactionClicks.current.delete(pendingKey);
      if (isClosingWorkflow) setLocalClosing(false);
    }
  };

  const handleForceClose = () => {
    if (!item?.messageId || !token || forceClosing || completing) return;
    setForceCloseConfirmOpen(true);
  };

  const confirmForceClose = async () => {
    const messageId = item?.messageId;
    if (!messageId || !token || forceClosing || completing) return;
    setForceCloseConfirmOpen(false);
    setForceClosing(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/workflows/${messageId}/force-close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = response.data;
      if (updated && typeof updated === 'object') {
        setLiveMessage((prev) => ({ ...(prev || item), ...updated }));
        if (updated.channelId) {
          dispatch(addMessage({
            channelId: updated.channelId,
            message: updated,
            currentUserId,
            skipUnreadCountIncrement: true,
          }));
        }
      }
      setFetchedTasks((prev) => (prev || []).map((task) => ({
        ...task,
        status: TaskStatus.COMPLETED,
        completionPercentage: 100,
        completionTimeInMinutes: Number(task?.completionTimeInMinutes) > 0
          ? task.completionTimeInMinutes
          : (Number(task?.estimateTimeInMinutes) > 0 ? task.estimateTimeInMinutes : 1),
      })));
      window.dispatchEvent(new CustomEvent('workloop:tasks-flushed'));
      window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', {
        detail: { messageId, action: 'completed' },
      }));
      dispatch(addNotification({ type: 'success', message: 'Workflow force closed' }));
      await new Promise((resolve) => window.setTimeout(resolve, 900));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err?.response?.data || err, 'Failed to force close workflow'),
      }));
    } finally {
      setForceClosing(false);
    }
  };

  const handleAssign = async (userId) => {
    if (!item.messageId || assigning) return;
    setAssigning(true);
    try {
      const updated = await dispatch(assignWorkflowMessage({ messageId: item.messageId, userId })).unwrap();
      setLiveMessage((prev) => ({ ...(prev || item), ...updated }));
      setAssignOpen(false);
      setAssignQuery('');
      dispatch(addNotification({ type: 'success', message: 'Assignee updated' }));
      window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', {
        detail: { messageId: item.messageId },
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to change assignee',
      }));
    } finally {
      setAssigning(false);
    }
  };

  const handleAddTag = async () => {
    const label = tagDraft.trim();
    if (!label || !item.messageId) return;
    setAddingTag(true);
    try {
      const updated = await dispatch(addMessageCustomTag({ messageId: item.messageId, label })).unwrap();
      setLiveMessage((prev) => ({ ...(prev || item), ...updated }));
      setTagDraft('');
      setAddingTagOpen(false);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to add tag',
      }));
    } finally {
      setAddingTag(false);
    }
  };

  const handleRemoveTag = async (tagId) => {
    if (!item.messageId || !tagId) return;
    try {
      const updated = await dispatch(removeMessageCustomTag({ messageId: item.messageId, tagId })).unwrap();
      setLiveMessage((prev) => ({ ...(prev || item), ...updated }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to remove tag',
      }));
    }
  };

  const handleSaveTrack = async ({ trackId: nextTrackId, trackLink: nextTrackLink, messageId, removed }) => {
    if (!messageId) return;
    try {
      const updated = await dispatch(updateWorkflowTrack({
        messageId,
        trackId: nextTrackId,
        trackLink: nextTrackLink,
        clear: Boolean(removed),
      })).unwrap();
      const nextTrack = {
        workflowTrackId: updated?.workflowTrackId || '',
        workflowTrackLink: updated?.workflowTrackLink || '',
      };
      setLiveMessage((prev) => ({
        ...(prev || item),
        ...updated,
        ...nextTrack,
      }));
      upsertRealmDocs('Message', [{
        ...(updated || {}),
        id: messageId,
        channelId: item.channelId || updated?.channelId,
        ...nextTrack,
        syncStatus: 'synced',
      }]).catch(() => {});
      if (item.channelId) {
        dispatch(addMessage({
          channelId: item.channelId,
          message: {
            ...(updated || {}),
            id: messageId,
            channelId: item.channelId,
            ...nextTrack,
          },
          currentUserId,
        }));
      }
      window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', {
        detail: { messageId },
      }));
      dispatch(addNotification({
        type: 'success',
        message: nextTrackId ? 'Track ID saved' : 'Track ID removed',
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: toErrorMessage(err, 'Failed to save track ID'),
      }));
    }
  };

  const startEditingTitle = () => {
    setTitleDraft(savedTitle || truncatePlainText(chatContent, 200) || '');
    setEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!item.messageId || savingTitle) return;
    const nextTitle = titleDraft.trim();
    setSavingTitle(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/messages/${item.messageId}/workflow-title`,
        { title: nextTitle },
        { headers: { Authorization: token ? `Bearer ${token}` : '' } },
      );
      const updated = response.data;
      if (!updated || typeof updated !== 'object' || updated.error) {
        throw new Error(toErrorMessage({ response: { data: updated } }, 'Failed to update title'));
      }
      const saved = typeof updated.workflowTitle === 'string' ? updated.workflowTitle : nextTitle;
      setLiveMessage((prev) => ({
        ...(prev || item),
        ...updated,
        content: (prev && typeof prev.content === 'string' ? prev.content : null)
          || item.contentHtml
          || item.content
          || updated.content,
        workflowTitle: saved,
      }));
      setEditingTitle(false);
      upsertRealmDocs('Message', [{
        ...updated,
        workflowTitle: saved,
        syncStatus: 'synced',
      }]).catch(() => {});
      if (item.channelId && updated.id) {
        dispatch(addMessage({
          channelId: item.channelId,
          message: { ...updated, workflowTitle: saved },
          currentUserId,
        }));
      }
      window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', {
        detail: { messageId: item.messageId },
      }));
      dispatch(addNotification({ type: 'success', message: 'Workflow title updated' }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: toErrorMessage(err, 'Failed to update title'),
      }));
    } finally {
      setSavingTitle(false);
    }
  };

  const handleSaveWorkflowSpace = async ({ spaceId, spaceName }) => {
    if (!item.messageId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/messages/${item.messageId}/workflow-space`,
        { spaceId, spaceName },
        { headers: { Authorization: token ? `Bearer ${token}` : '' } },
      );
      const updated = response.data;
      if (!updated || typeof updated !== 'object' || updated.error) {
        throw new Error(toErrorMessage({ response: { data: updated } }, 'Failed to update space'));
      }
      setLiveMessage((prev) => ({
        ...(prev || item),
        ...updated,
        content: (prev && typeof prev.content === 'string' ? prev.content : null)
          || item.contentHtml
          || item.content
          || updated.content,
      }));
      upsertRealmDocs('Message', [{
        ...updated,
        syncStatus: 'synced',
      }]).catch(() => {});
      if (item.channelId && updated.id) {
        dispatch(addMessage({
          channelId: item.channelId,
          message: updated,
          currentUserId,
        }));
      }
      window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', {
        detail: { messageId: item.messageId },
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: toErrorMessage(err, 'Failed to update space'),
      }));
    }
  };

  const startEditingDescription = () => {
    setDescriptionDraft(description || '');
    setEditingDescription(true);
  };

  const handleSaveDescription = async () => {
    if (!item.messageId || savingDescription) return;
    const editor = descriptionQuillRef.current?.getEditor();
    const html = editor ? editor.root.innerHTML : descriptionDraft;
    setSavingDescription(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/messages/${item.messageId}/workflow-content`,
        { content: html || '' },
        { headers: { Authorization: token ? `Bearer ${token}` : '' } },
      );
      const updated = response.data;
      if (!updated || typeof updated !== 'object' || updated.error) {
        throw new Error(toErrorMessage({ response: { data: updated } }, 'Failed to update workflow'));
      }
      const nextDescription = typeof updated.workflowDescription === 'string'
        ? updated.workflowDescription
        : html;
      setLiveMessage((prev) => ({
        ...(prev || item),
        ...updated,
        content: (prev && typeof prev.content === 'string' ? prev.content : null)
          || item.contentHtml
          || item.content
          || updated.content,
        workflowDescription: nextDescription,
      }));
      setEditingDescription(false);
      upsertRealmDocs('Message', [{
        ...updated,
        workflowDescription: nextDescription,
        syncStatus: 'synced',
      }]).catch(() => {});
      if (item.channelId && updated.id) {
        dispatch(addMessage({
          channelId: item.channelId,
          message: { ...updated, workflowDescription: nextDescription },
          currentUserId,
        }));
      }
      window.dispatchEvent(new CustomEvent('workloop:workflows-assigned-changed', {
        detail: { messageId: item.messageId },
      }));
      dispatch(addNotification({ type: 'success', message: 'Workflow description updated' }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: toErrorMessage(err, 'Failed to update workflow'),
      }));
    } finally {
      setSavingDescription(false);
    }
  };

  const handlePostCompletionChange = async ({ enabled, actions }) => {
    if (!item?.messageId) return;
    const previous = liveMessage || item;
    setLiveMessage((prev) => ({
      ...(prev || item),
      postCompletionEnabled: enabled,
      postCompletionActions: actions,
    }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/messages/${item.messageId}/post-completion`,
        { postCompletionEnabled: enabled, postCompletionActions: actions },
        { headers: { Authorization: token ? `Bearer ${token}` : '' } },
      );
      const updated = response.data;
      if (!updated || typeof updated !== 'object' || updated.error) {
        throw new Error(toErrorMessage({ response: { data: updated } }, 'Failed to save post completion'));
      }
      setLiveMessage(updated);
    } catch (err) {
      setLiveMessage(previous);
      dispatch(addNotification({
        type: 'error',
        message: toErrorMessage(err, 'Failed to save post completion'),
      }));
    }
  };

  const insertCaptionIntoComment = (caption) => {
    const html = captionToHtml(caption);
    if (!htmlIsMeaningful(html)) return;
    const editor = commentQuillRef.current?.getEditor?.();
    if (!editor) {
      setCommentDraft((prev) => (htmlIsMeaningful(prev) ? `${prev}${html}` : html));
      return;
    }
    const current = editor.root.innerHTML;
    const next = htmlIsMeaningful(current) ? `${current}${html}` : html;
    editor.clipboard.dangerouslyPasteHTML(next);
    setCommentDraft(editor.root.innerHTML);
  };

  const handleAddComment = async ({ html: htmlOverride, attachments = [] } = {}) => {
    const editor = commentQuillRef.current?.getEditor();
    const html = htmlOverride != null ? htmlOverride : (editor ? editor.root.innerHTML : commentDraft);
    const hasAttachments = Array.isArray(attachments) && attachments.length > 0;
    if ((!htmlIsMeaningful(html) && !hasAttachments) || !item.channelId || !item.messageId) return;
    const plain = editor?.getText?.() || stripHtmlTags(html);
    const mentionedUserIds = collectMentionedUserIdsFromPlain(
      plain,
      users,
      currentUserId,
      channelMemberIds,
    );
    setSendingComment(true);
    try {
      const sent = await dispatch(sendMessage({
        content: htmlIsMeaningful(html) ? html : '',
        channelId: item.channelId,
        replyToMessageId: item.messageId,
        senderId: currentUserId,
        mentionedUserIds,
        attachments: hasAttachments ? attachments : undefined,
      })).unwrap();
      if (sent) {
        shouldStickToLatestComment.current = true;
        setComments((prev) => mergeComments(prev, [{
          ...sent,
          senderId: sent.senderId || currentUserId,
          senderName: sent.senderName || currentUserName,
          senderProfilePicture: sent.senderProfilePicture || currentUser?.profilePicture,
          createdAt: sent.createdAt || sent.timestamp || new Date().toISOString(),
        }]));
      }
      setCommentDraft('');
      if (editor) editor.setText('');
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: toErrorMessage(err, 'Failed to add comment'),
      }));
    } finally {
      setSendingComment(false);
    }
  };

  const sendCommentVoice = async ({ includeAudio, includeCaption = true, caption: captionOverride } = {}) => {
    const review = commentVoiceRef.current?.review;
    if (!review || sendingComment) return;
    const caption = includeCaption
      ? String(captionOverride ?? commentVoiceRef.current?.getCaption?.() ?? review.caption ?? '').trim()
      : '';
    if (!includeAudio && !caption) {
      dispatch(addNotification({
        type: 'error',
        message: 'Choose audio only, caption only, or audio with caption.',
      }));
      return;
    }
    if (includeCaption && !caption) {
      dispatch(addNotification({
        type: 'error',
        message: 'Type a caption in the box, or send audio only.',
      }));
      return;
    }
    if (!includeAudio) {
      insertCaptionIntoComment(caption);
      commentVoiceRef.current?.discardReview();
      return;
    }
    try {
      setSendingComment(true);
      if (!review.blob || review.blob.size < 64) {
        throw new Error('Voice note audio is empty');
      }
      let durationSeconds = Number(review.durationSeconds);
      if (!Number.isFinite(durationSeconds) || durationSeconds < 0.5) {
        durationSeconds = await readBlobDurationSeconds(review.blob);
      }
      const voiceFile = await blobToVoiceFile(review.blob, durationSeconds);
      const results = await uploadFilesWithProgress({
        apiUrl: API_URL,
        token: token || localStorage.getItem('token'),
        files: [{ file: voiceFile, fileType: 'AUDIO' }],
        timeout: 120000,
      });
      const voiceAttachments = results.map(({ data, fileType }) => {
        const file = { ...data };
        file.fileType = inferAttachmentFileType({ ...file, fileType: fileType || file.fileType || 'AUDIO' });
        if (Number.isFinite(durationSeconds) && durationSeconds >= 0.5) {
          file.durationSeconds = durationSeconds;
        }
        return file;
      });
      commentVoiceRef.current?.discardReview();
      await handleAddComment({
        html: includeCaption ? captionToHtml(caption) : '',
        attachments: voiceAttachments,
      });
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: toErrorMessage(err, 'Failed to send voice comment'),
      }));
    } finally {
      setSendingComment(false);
    }
  };

  const handleEditComment = async (comment, content) => {
    const id = commentKey(comment);
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/messages/${id}`,
        { content },
        { headers: { Authorization: token ? `Bearer ${token}` : '' } },
      );
      const updated = {
        ...comment,
        ...response.data,
        isEdited: true,
      };
      setComments((prev) => mergeComments(prev, [updated]));
      if (updated.channelId) {
        dispatch(addMessage({
          channelId: updated.channelId,
          message: updated,
          skipUnreadCountIncrement: true,
        }));
      }
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: toErrorMessage(err, 'Failed to edit comment'),
      }));
      throw err;
    }
  };

  const handleDeleteComment = async (comment) => {
    const id = commentKey(comment);
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/messages/${id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      setComments((prev) => removeCommentById(prev, id));
      if (comment.channelId) {
        dispatch(removeMessage({ channelId: comment.channelId, messageId: id }));
      }
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: toErrorMessage(err, 'Failed to delete comment'),
      }));
      throw err;
    }
  };

  const unfinishedLinked = unfinishedWorkflowLinkedCount(linkedTasks, mappedByTaskId, title);
  const canCompleteWorkflow = unfinishedLinked === 0;
  const displayLinkedTasks = visibleWorkflowLinkedTasks(linkedTasks, item?.messageId, title);
  const hasCommentDraft = htmlIsMeaningful(commentDraft);
  const taskSaving = savingTask || savingLocalTask;

  const handleAddLinkedTask = async (payload) => {
    const assigneeUserId = payload?.assigneeUserId || defaultTaskAssigneeId || currentUserId;
    const delegateId = assigneeUserId && String(assigneeUserId) !== String(currentUserId)
      ? [assigneeUserId]
      : [];
    const nextPayload = {
      title: payload.title,
      estimateTimeInMinutes: payload.estimateTimeInMinutes,
      assigneeUserId,
      delegateId,
    };
    if (onAddTask) {
      await onAddTask(nextPayload);
      return;
    }
    if (!item?.messageId) return;
    setSavingLocalTask(true);
    try {
      const created = await dispatch(createTask({
        title: nextPayload.title,
        estimateTimeInMinutes: nextPayload.estimateTimeInMinutes,
        status: TaskStatus.PICK_TODAY,
        messageId: item.messageId,
        delegateId,
        spaceId: message.workflowSpaceId || item.workflowSpaceId || '',
        spaceName: message.workflowSpaceName || item.workflowSpaceName || '',
      })).unwrap();
      if (created?.id) {
        setFetchedTasks((prev) => [...prev.filter((task) => task.id !== created.id), created]);
      }
      window.dispatchEvent(new CustomEvent('workloop:tasks-flushed'));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : (err?.message || 'Failed to add task'),
      }));
      throw err;
    } finally {
      setSavingLocalTask(false);
    }
  };

  const handleCloneLinkedTask = async (task) => {
    if (!task?.id || isClosed) return;
    const messageId = task.messageId || item?.messageId;
    if (!messageId) return;
    const existingTags = Array.isArray(task.tags)
      ? task.tags.map((tag) => String(tag).trim()).filter(Boolean)
      : [];
    const tags = existingTags.some((tag) => tag.toLowerCase() === 'cloned')
      ? existingTags
      : [...existingTags, 'cloned'];
    const sourceDelegates = Array.isArray(task.delegateId)
      ? task.delegateId.filter(Boolean)
      : (task.delegateId ? [task.delegateId] : []);
    setSavingLocalTask(true);
    try {
      const created = await dispatch(createTask({
        title: (task.title || '').trim() || 'Task',
        description: task.description || '',
        estimateTimeInMinutes: task.estimateTimeInMinutes,
        status: TaskStatus.PICK_TODAY,
        messageId,
        workflowMessageIds: Array.isArray(task.workflowMessageIds) ? task.workflowMessageIds : undefined,
        delegateId: sourceDelegates,
        tags,
        spaceId: item.workflowSpaceId || task.spaceId || '',
        spaceName: item.workflowSpaceName || task.spaceName || '',
      })).unwrap();
      if (created?.id) {
        setFetchedTasks((prev) => [...prev.filter((itemTask) => itemTask.id !== created.id), created]);
        setViewingLinkedTask(created);
      }
      window.dispatchEvent(new CustomEvent('workloop:tasks-flushed'));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : (err?.message || 'Failed to clone task'),
      }));
    } finally {
      setSavingLocalTask(false);
    }
  };

  const handleEditLinkedTask = async (task, payload) => {
    if (onEditTask) {
      await onEditTask(task, payload);
      return;
    }
    if (!task?.id) return;
    setSavingLocalTask(true);
    try {
      const updated = await dispatch(updateTask({
        taskId: task.id,
        taskData: {
          title: payload.title,
          estimateTimeInMinutes: payload.estimateTimeInMinutes,
        },
      })).unwrap();
      setFetchedTasks((prev) => prev.map((itemTask) => (
        itemTask.id === task.id
          ? { ...itemTask, ...(updated || {}), title: payload.title, estimateTimeInMinutes: payload.estimateTimeInMinutes }
          : itemTask
      )));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : (err?.message || 'Failed to update task'),
      }));
      throw err;
    } finally {
      setSavingLocalTask(false);
    }
  };

  const dialog = (
    <div
      className={`fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center ${zIndexClass} p-4`}
      onClick={() => {
        if (completing || forceClosing || localClosing) return;
        if (showReactionPicker) {
          setShowReactionPicker(false);
          return;
        }
        onClose();
      }}
    >
      <div
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-5xl w-full h-[90vh] max-h-[90vh] overflow-hidden flex flex-col border border-gray-100 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <WorkCloseProgressOverlay
          open={completing || forceClosing || localClosing}
          variant={forceClosing ? 'force-workflow' : 'workflow'}
        />
        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/35 dark:text-indigo-300">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Workflow
              </p>
              {typeDisplay ? (
                <span className={`${chipClass(typeDisplay.tone)} mt-1`} title={typeDisplay.label}>
                  <span aria-hidden="true">{typeDisplay.emoji}</span>
                  <span className="truncate">{typeDisplay.label}</span>
                </span>
              ) : null}
              {editingTitle ? (
                <div className="mt-1 space-y-2">
                  <input
                    type="text"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value.slice(0, 200))}
                    maxLength={200}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSaveTitle();
                      }
                      if (e.key === 'Escape') setEditingTitle(false);
                    }}
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-1.5 text-base font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                    placeholder="Workflow title"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSaveTitle}
                      disabled={savingTitle}
                      className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
                    >
                      {savingTitle ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingTitle(false)}
                      disabled={savingTitle}
                      className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <EnableLiveTrackingButton
                      itemType="WORKFLOW"
                      sourceMessageId={item?.messageId || message?.id}
                      sourceChannelId={item?.channelId || message?.channelId}
                      title={titleDraft || title}
                      assigneeUserId={assigneeId || undefined}
                      assigneeName={assigneeName || undefined}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 flex-wrap">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-snug break-words min-w-0">
                    {title}
                  </h2>
                  <button
                    type="button"
                    onClick={startEditingTitle}
                    className="mt-0.5 flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-300 dark:hover:bg-gray-700 transition-colors"
                    title="Edit title"
                    aria-label="Edit workflow title"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M4 20h4.586a1 1 0 00.707-.293l9.414-9.414a2 2 0 000-2.828l-2.172-2.172a2 2 0 00-2.828 0L4.293 14.707A1 1 0 004 15.414V20z" />
                    </svg>
                  </button>
                  <EnableLiveTrackingButton
                    itemType="WORKFLOW"
                    sourceMessageId={item?.messageId || message?.id}
                    sourceChannelId={item?.channelId || message?.channelId}
                    title={title}
                    assigneeUserId={assigneeId || undefined}
                    assigneeName={assigneeName || undefined}
                  />
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {statusDisplay ? (
                  <span className={`${chipClass(statusDisplay.tone)}`} title={statusDisplay.label}>
                    <span aria-hidden="true">{statusDisplay.emoji}</span>
                    <span className="truncate">{statusDisplay.label}</span>
                  </span>
                ) : null}
                <div
                  ref={reactionTriggerRef}
                  data-workflow-reaction-trigger
                  className="relative flex-shrink-0"
                >
                  <button
                    type="button"
                    onClick={() => setShowReactionPicker((open) => !open)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 hover:text-primary-600 hover:border-primary-400 dark:hover:text-primary-300"
                    title="Add icon"
                    aria-label="Add icon"
                    aria-expanded={showReactionPicker}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                </div>
                {onNavigateToChat ? (
                  <button
                    type="button"
                    onClick={onNavigateToChat}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 border border-primary-200/80 dark:border-primary-800/60 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                    title="Open in chat"
                    aria-label="Open in chat"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Chat
                  </button>
                ) : null}
              </div>
              {isClosed && completionTimeLabel ? (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/35 dark:text-emerald-300 px-2.5 py-1 text-[12px] font-semibold tabular-nums">
                  <ClockIcon className="w-3.5 h-3.5" />
                  Finished in {completionTimeLabel}
                </div>
              ) : null}
              {!isClosed && messageHasUrgentWorkflow(resolvedReactions)
                && (message.urgentDeadlineAt || item.urgentDeadlineAt) ? (
                <div className="mt-2">
                  <UrgentCountdownBadge
                    deadlineAt={message.urgentDeadlineAt || item.urgentDeadlineAt}
                    blink={false}
                  />
                </div>
              ) : null}
            </div>
            {isClosed ? (
              <span className="flex-shrink-0 self-center pr-1 text-2xl sm:text-3xl font-black uppercase tracking-wide text-emerald-600 dark:text-emerald-400 leading-none">
                Completed
              </span>
            ) : null}
            <div className="flex-shrink-0 self-start pt-0.5">
              <SpacePicker
                variant="combo"
                valueId={message.workflowSpaceId || item.workflowSpaceId || ''}
                valueName={message.workflowSpaceName || item.workflowSpaceName || ''}
                onChange={handleSaveWorkflowSpace}
              />
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col sm:flex-row overflow-hidden">
          <div className="w-full sm:w-1/2 min-w-0 min-h-0 flex flex-col overflow-hidden border-b sm:border-b-0 sm:border-r border-gray-100 dark:border-gray-700">
            <div className="flex-shrink-0 px-5 pt-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-1">
              {[
                { id: 'details', label: 'Details' },
                { id: 'completion', label: 'Post completion' },
                { id: 'history', label: 'History' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setPopupTab(tab.id)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                    popupTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-300'
                      : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              <div className="ml-auto mb-1 inline-flex items-center gap-2">
                <span
                  className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-300"
                  title={typeDisplay?.label || 'Workflow'}
                >
                  {typeDisplay?.emoji ? <span className="text-base leading-none normal-case" aria-hidden="true">{typeDisplay.emoji}</span> : null}
                  <span className="truncate max-w-[12rem]">{typeDisplay?.label || 'Workflow'}</span>
                </span>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
              {popupTab === 'completion' ? (
                <PostCompletionEditor
                  enabled={Boolean(message.postCompletionEnabled) || (message.postCompletionActions || []).length > 0}
                  actions={message.postCompletionActions || []}
                  users={users || []}
                  channels={(channels || []).filter((c) => {
                    if (!c || c.systemChannelKey) return false;
                    const type = String(c.type || '').toUpperCase();
                    return type !== 'DIRECT_MESSAGE' && type !== 'DIRECT' && type !== 'DM';
                  })}
                  channelName={channel?.name || ''}
                  disabled={Boolean(isClosed)}
                  title="Post completion"
                  description="When this workflow is marked complete, these follow-ups run for the people you choose."
                  onChange={handlePostCompletionChange}
                />
              ) : popupTab === 'history' ? (
                <WorkItemHistoryPanel
                  entityLabel="workflow"
                  endpoint={(item?.messageId || message?.id) ? `/messages/${item?.messageId || message.id}/workflow-history` : ''}
                  reloadToken={popupTab}
                  fallback={message?.createdAt ? {
                    action: 'CREATED',
                    summary: 'Workflow created',
                    actorName: message.senderName || 'Someone',
                    createdAt: message.createdAt,
                  } : null}
                />
              ) : (
              <>
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Assigned to
                </h3>
                <div className="w-full max-w-sm">
                  <WorkflowAssigneeDropdown
                    hideLabel
                    users={assignableUsers}
                    selectedUserId={assigneeId}
                    selectedUserName={
                      assigneeId && String(assigneeId) === String(currentUserId)
                        ? `You (${assigneeName || 'me'})`
                        : (assigneeName || '')
                    }
                    isOpen={assignOpen}
                    searchQuery={assignQuery}
                    onToggleOpen={(open) => {
                      if (assigning) return;
                      setAssignOpen(open);
                      if (!open) setAssignQuery('');
                    }}
                    onSearchChange={setAssignQuery}
                    onSelect={handleAssign}
                  />
                </div>
              </section>

              {isClosed && completionTimeLabel ? (
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Time spent
                </h3>
                <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300 tabular-nums">
                  <ClockIcon className="w-4 h-4" />
                  {completionTimeLabel}
                </p>
              </section>
              ) : null}

              <section>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Tasks
                    {displayLinkedTasks.length > 0 ? (
                      <span className="ml-1.5 normal-case tracking-normal text-gray-400">
                        {displayLinkedTasks.length}
                      </span>
                    ) : null}
                  </h3>
                </div>
                <div className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                  {displayLinkedTasks.length === 0 && isClosed ? (
                    <p className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500">No tasks</p>
                  ) : (
                    displayLinkedTasks.map((task) => (
                      <TaskSubtaskItem
                        key={task.id}
                        subtask={task}
                        indent={false}
                        busy={taskSaving}
                        readOnly={isClosed}
                        assigneeLabel={taskAssigneeLabel(task)}
                        onOpen={() => setViewingLinkedTask(task)}
                        onEdit={!isClosed ? (payload) => handleEditLinkedTask(task, payload) : undefined}
                        onClone={!isClosed ? () => handleCloneLinkedTask(task) : undefined}
                        onDelete={onDeleteTask ? () => onDeleteTask(task) : undefined}
                        onComplete={onCompleteTask && !isClosed ? () => onCompleteTask(task) : undefined}
                        timerDisabled={workTimerDisabled || isClosed}
                      />
                    ))
                  )}
                  {!isClosed ? (
                    addingLinkedTask ? (
                      <QuickTaskComposer
                        stacked
                        compact
                        flush
                        autoFocus
                        placeholder="Task title"
                        submitLabel="Add"
                        saving={taskSaving}
                        assignees={assignableUsers}
                        defaultAssigneeId={defaultTaskAssigneeId}
                        onSubmit={async (payload) => {
                          await handleAddLinkedTask(payload);
                          setAddingLinkedTask(false);
                        }}
                        onCancel={() => setAddingLinkedTask(false)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAddingLinkedTask(true)}
                        className="w-full flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50/80 dark:text-primary-400 dark:hover:text-primary-300 dark:hover:bg-primary-900/20"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 5v14M5 12h14" />
                        </svg>
                        Add task
                      </button>
                    )
                  ) : null}
                </div>
              </section>

              <section>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Tags
                    </h3>
                    <div className="flex flex-wrap items-center gap-1.5">
                  {workflowReactions.map((reaction, reactionIndex) => (
                    <MessageReactionBadge
                      key={`wf-${reaction.emoji || reactionIndex}`}
                      reactionId={reaction.emoji}
                      count={reaction.userIds?.length || 0}
                      isOwn={false}
                      isWorkflow
                      onClick={() => handleReactionClick(reaction.emoji)}
                    />
                  ))}
                  {freeReactions.map((reaction, reactionIndex) => {
                    const isActive = currentUserId != null
                      && Array.isArray(reaction.userIds)
                      && reaction.userIds.some((id) => String(id) === String(currentUserId));
                    return (
                      <MessageReactionBadge
                        key={`free-${reaction.emoji || reactionIndex}`}
                        reactionId={reaction.emoji}
                        count={reaction.userIds?.length || 0}
                        isOwn={false}
                        isActive={isActive}
                        onClick={() => handleReactionClick(reaction.emoji)}
                      />
                    );
                  })}
                  {customTags.map((tag) => (
                    <span key={tag.id} className={`${chipClass('indigo')} group/tag`}>
                      <span className="truncate">{tag.label}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag.id)}
                        className="opacity-0 group-hover/tag:opacity-100 ml-0.5"
                        title="Remove tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {addingTagOpen ? (
                    <form
                      className="inline-flex items-center gap-1"
                      onSubmit={(event) => {
                        event.preventDefault();
                        handleAddTag();
                      }}
                    >
                      <input
                        ref={tagInputRef}
                        type="text"
                        value={tagDraft}
                        onChange={(event) => setTagDraft(event.target.value)}
                        onBlur={() => {
                          if (!tagDraft.trim() && !addingTag) setAddingTagOpen(false);
                        }}
                        maxLength={64}
                        placeholder="Tag"
                        className="h-7 w-28 text-[12px] rounded-md border border-primary-300 dark:border-primary-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                      />
                      <button
                        type="submit"
                        disabled={addingTag || !tagDraft.trim()}
                        className="h-7 px-2 text-[11px] font-semibold rounded-md text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
                      >
                        Add
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setAddingTagOpen(true);
                        setTimeout(() => tagInputRef.current?.focus(), 0);
                      }}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 hover:text-primary-600 hover:border-primary-400 dark:hover:text-primary-300"
                      title="Add custom tag"
                      aria-label="Add custom tag"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 5v14M5 12h14" />
                      </svg>
                    </button>
                  )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Track ID
                    </h3>
                    {trackId ? (
                      <div className="inline-flex items-center gap-1">
                        {trackLink ? (
                          <a
                            href={trackLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${chipClass('indigo')} hover:border-indigo-400`}
                            title={`Open ${trackId}`}
                          >
                            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            <span className="truncate">{trackId}</span>
                          </a>
                        ) : (
                          <span className={chipClass('indigo')} title={trackId}>
                            {trackId}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowTrackDialog(true)}
                          className="p-1 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-300 dark:hover:bg-gray-700"
                          title="Edit Track ID"
                          aria-label="Edit Track ID"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M4 20h4.586a1 1 0 00.707-.293l9.414-9.414a2 2 0 000-2.828l-2.172-2.172a2 2 0 00-2.828 0L4.293 14.707A1 1 0 004 15.414V20z" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowTrackDialog(true)}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-[11px] font-semibold text-gray-600 hover:text-primary-600 hover:border-primary-400 dark:text-gray-300 dark:hover:text-primary-300"
                        title="Add Track ID"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 5v14M5 12h14" />
                        </svg>
                        TrackId
                      </button>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Raised by
                </h3>
                <div className="flex items-center gap-2">
                  <PresignedAvatar
                    fileKey={item.senderProfilePicture}
                    name={item.senderName}
                    className="w-7 h-7 rounded-full object-cover"
                    fallbackClassName="w-7 h-7 bg-primary-500 rounded-full flex items-center justify-center text-white text-[11px] font-medium"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {item.senderName || 'Unknown User'}
                  </span>
                  {raisedAgo ? (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      · {raisedAgo}
                    </span>
                  ) : null}
                </div>
              </section>

              <section>
                <div className="flex items-center gap-1.5 mb-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Description
                  </h3>
                  {!editingDescription && (
                    <button
                      type="button"
                      onClick={startEditingDescription}
                      className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-300 dark:hover:bg-gray-700 transition-colors"
                      title="Edit description"
                      aria-label="Edit workflow description"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M4 20h4.586a1 1 0 00.707-.293l9.414-9.414a2 2 0 000-2.828l-2.172-2.172a2 2 0 00-2.828 0L4.293 14.707A1 1 0 004 15.414V20z" />
                      </svg>
                    </button>
                  )}
                </div>
                {editingDescription ? (
                  <div className="space-y-2">
                    <CompactRichEditor
                      quillRef={descriptionQuillRef}
                      value={descriptionDraft}
                      onChange={setDescriptionDraft}
                      placeholder="Write the workflow description…"
                      minHeight={120}
                      maxHeight={220}
                      instanceClass="workflow-dialog-quill-desc"
                      channelMemberIds={channelMemberIds}
                    />
                    {sourceAttachments.length > 0 ? (
                      <div className="mt-2" onClick={(event) => event.stopPropagation()}>
                        <AttachmentGroupBar
                          attachments={sourceAttachments}
                          groupName={sourceAttachmentGroupName}
                          expanded
                          onViewImage={handleViewSourceImage}
                          onDownload={handleDownloadSource}
                          mode="display"
                        />
                      </div>
                    ) : null}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSaveDescription}
                        disabled={savingDescription}
                        className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
                      >
                        {savingDescription ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingDescription(false)}
                        disabled={savingDescription}
                        className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                  <button
                    type="button"
                    onClick={startEditingDescription}
                    className="w-full text-left text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/60 rounded-xl px-3 py-2.5 break-words hover:bg-gray-100 dark:hover:bg-gray-800/80"
                    title="Edit description"
                  >
                    {renderRichOrPlain(description, sourceAttachments.length ? '' : 'Add a description…')}
                  </button>
                    {sourceAttachments.length > 0 ? (
                      <div onClick={(event) => event.stopPropagation()}>
                        <AttachmentGroupBar
                          attachments={sourceAttachments}
                          groupName={sourceAttachmentGroupName}
                          expanded
                          onViewImage={handleViewSourceImage}
                          onDownload={handleDownloadSource}
                          mode="display"
                        />
                      </div>
                    ) : null}
                  </div>
                )}
              </section>
              </>
              )}
            </div>
          </div>

          <div className="w-full sm:w-1/2 min-w-0 min-h-[42%] sm:min-h-0 flex flex-col overflow-hidden bg-gray-50/70 dark:bg-gray-900/25">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Chat
              </h3>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                {mappedChats.length + comments.length > 0
                  ? `${mappedChats.length + comments.length}`
                  : null}
              </span>
            </div>

            <div
              ref={commentsScrollRef}
              className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4"
              onScroll={(event) => {
                const el = event.currentTarget;
                const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
                shouldStickToLatestComment.current = remaining < 48;
              }}
            >
              <div className="space-y-3 min-w-0">
                {mappedChats.length === 0 && comments.length === 0 ? (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    No mapped chats or comments yet
                  </p>
                ) : null}

                {mappedChats.length > 0 ? (
                  <>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 pt-0.5">
                      Mapped chats
                    </p>
                    {mappedChats.map((mappedTask) => {
                      const mappedMessage = mappedMessagesByTaskId[mappedTask.id];
                      if (mappedMessage) {
                        return (
                          <ChatMessageBubblePreview
                            key={`wf-chat-${mappedTask.id}`}
                            contentHtml={mappedMessage.content}
                            attachments={mappedMessage.attachments}
                            attachmentGroupName={mappedMessage.attachmentGroupName}
                            linkPreview={mappedMessage.linkPreview}
                            reactions={mappedMessage.reactions}
                            workflowTitle={mappedMessage.workflowTitle}
                            senderName={mappedMessage.senderName}
                            senderProfilePicture={mappedMessage.senderProfilePicture}
                            createdAt={mappedMessage.createdAt || mappedMessage.timestamp}
                            alignLeft
                            compact
                            onViewImage={handleViewSourceImage}
                            onDownload={handleDownloadSource}
                          />
                        );
                      }
                      return (
                        <div
                          key={`wf-chat-fallback-${mappedTask.id}`}
                          className="text-sm bg-white dark:bg-gray-800 rounded-xl px-3 py-2.5 border border-gray-100 dark:border-gray-700 break-words"
                        >
                          {renderRichOrPlain(mappedTask.description, 'Mapped chat')}
                        </div>
                      );
                    })}
                  </>
                ) : null}

                {comments.length > 0 || mappedChats.length > 0 ? (
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 pt-2">
                    Comments
                  </p>
                ) : null}

                {comments.length === 0 ? (
                  mappedChats.length > 0 ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500">No comments yet</p>
                  ) : null
                ) : (
                  comments.map((comment) => (
                    <ChatMessageBubblePreview
                      key={commentKey(comment)}
                      contentHtml={comment.content}
                      attachments={comment.attachments}
                      senderName={comment.senderName}
                      senderProfilePicture={comment.senderProfilePicture}
                      createdAt={comment.createdAt || comment.timestamp}
                      isOwn={String(comment.senderId) === String(currentUserId)}
                      isEdited={Boolean(comment.isEdited || comment.edited)}
                      alignLeft
                      compact
                      onViewImage={handleViewSourceImage}
                      onDownload={handleDownloadSource}
                      onSaveEdit={String(comment.senderId) === String(currentUserId)
                        ? (html) => handleEditComment(comment, html)
                        : undefined}
                      onDelete={String(comment.senderId) === String(currentUserId)
                        ? () => handleDeleteComment(comment)
                        : undefined}
                    />
                  ))
                )}
                <div ref={commentsEndRef} />
              </div>
            </div>

            <div className="flex-shrink-0 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
              <VoiceNoteBanner
                recording={commentVoice.recording}
                seconds={commentVoice.seconds}
                caption={commentVoice.liveCaption}
                captionAfterStop={Boolean(window.workloopRealm)}
                speechSupported={commentVoice.speechSupported}
                speechHint={commentVoice.speechHint}
                captionPrompt={commentVoice.captionPrompt}
                onAllowCaptions={() => commentVoice.startSpeechRecognition()}
                onDeclineCaptions={() => commentVoice.declineCaptionPermission()}
                onStop={() => commentVoice.stopRecording()}
                onCancel={() => commentVoice.cancelRecording()}
              />
              <CompactRichEditor
                quillRef={commentQuillRef}
                value={commentDraft}
                onChange={setCommentDraft}
                placeholder="Write a comment…"
                minHeight={88}
                maxHeight={140}
                instanceClass="workflow-dialog-quill-comment"
                channelMemberIds={channelMemberIds}
              />
              <div className="mt-2 flex items-center gap-2">
                <VoiceNoteMicButton
                  recording={commentVoice.recording}
                  disabled={sendingComment}
                  onClick={() => {
                    if (commentVoice.recording) {
                      commentVoice.stopRecording();
                      return;
                    }
                    commentVoice.startRecording();
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleAddComment()}
                  disabled={sendingComment || !hasCommentDraft}
                  className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
                >
                  {sendingComment ? 'Sending…' : 'Comment'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/30 flex-shrink-0">
          {!isClosed && (
            <button
              type="button"
              onClick={onComplete || (() => handleReactionClick(WORKFLOW_CLOSED_STATUS_ID))}
              disabled={completing || forceClosing || !canCompleteWorkflow}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 disabled:opacity-50"
              title={!canCompleteWorkflow ? 'Finish all workflow tasks first' : undefined}
            >
              {completing ? 'Closing…' : 'Close workflow'}
            </button>
          )}
          {!isClosed && isAdminUser ? (
            <button
              type="button"
              onClick={handleForceClose}
              disabled={completing || forceClosing}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400 disabled:opacity-50"
              title="Complete all linked tasks and close this workflow"
            >
              {forceClosing ? 'Force closing…' : 'Force close'}
            </button>
          ) : null}
          {onUndoComplete && isClosed && (
            <button
              type="button"
              onClick={onUndoComplete}
              disabled={completing}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-amber-700 hover:text-amber-800 dark:text-amber-300 disabled:opacity-50"
              title="Undo completion and revert time spent"
            >
              {completing ? 'Undoing…' : 'Undo'}
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] font-semibold rounded-lg text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30"
          >
            Share
          </button>
          {onNavigateToChat && (
            <button
              type="button"
              onClick={onNavigateToChat}
              className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30"
            >
              Open in chat
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return (
      <>
        {ReactDOM.createPortal(dialog, document.body)}
        {shareOpen ? (
          <WorkItemShareMenu
            share={{
              kind: WORK_ITEM_SHARE_KIND.WORKFLOW,
              itemId: item.messageId,
              title,
              channelId: item.channelId,
            }}
            onClose={() => setShareOpen(false)}
          />
        ) : null}
        {urgentDeadlineDialog}
        {viewingGallery ? (
          <ImageViewerModal
            images={viewingGallery.images}
            initialIndex={viewingGallery.index}
            onClose={() => setViewingGallery(null)}
            onDownload={handleDownloadSource}
          />
        ) : null}
        <WorkflowTrackDialog
          isOpen={showTrackDialog}
          onClose={() => setShowTrackDialog(false)}
          initialTrackId={trackId}
          initialTrackLink={trackLink}
          messageId={item.messageId}
          onSave={handleSaveTrack}
          zIndexClass="z-[1100]"
        />
        {showReactionPicker && reactionPickerPosition && ReactDOM.createPortal(
          <ReactionPicker
            initialTab="workflow"
            hideCategories={['work-item-type']}
            onSelect={handleReactionClick}
            currentReactions={resolvedReactions}
            currentUserId={currentUserId}
            style={{
              position: 'fixed',
              top: reactionPickerPosition.top,
              left: reactionPickerPosition.left,
              zIndex: REACTION_PICKER_Z_INDEX,
            }}
          />,
          document.body,
        )}
        {viewingLinkedTask ? (
          <TaskViewDialog
            task={linkedTasks.find((itemTask) => itemTask.id === viewingLinkedTask.id) || viewingLinkedTask}
            onClose={() => setViewingLinkedTask(null)}
            usePortal
            zIndexClass="z-[1100]"
            onDelegated={(updated) => {
              if (!updated?.id) return;
              setFetchedTasks((prev) => uniqueTasksById(prev, updated));
              setViewingLinkedTask((prev) => (
                prev?.id === updated.id ? { ...prev, ...updated } : prev
              ));
            }}
            onNavigateToMessage={(navTask) => {
              setViewingLinkedTask(null);
              window.dispatchEvent(new CustomEvent('workloop:go-to-message', {
                detail: {
                  messageId: navTask?.messageId,
                  channelId: navTask?.channelId || item?.channelId,
                  replyToMessageId: navTask?.replyToMessageId || null,
                },
              }));
            }}
            onOpenWorkflow={(opened) => {
              if (viewingLinkedTask?.id) {
                setFetchedTasks((prev) => uniqueTasksById(viewingLinkedTask, prev));
              }
              setViewingLinkedTask(null);
              if (opened?.messageId && opened.messageId !== item?.messageId) {
                window.dispatchEvent(new CustomEvent('workloop:open-workflow', { detail: opened }));
              }
            }}
            onCompleteTask={onCompleteTask && !isClosed
              ? async () => {
                await onCompleteTask(viewingLinkedTask);
                setViewingLinkedTask(null);
              }
              : undefined}
          />
        ) : null}
      </>
    );
  }
  return (
    <>
      {dialog}
      {urgentDeadlineDialog}
      {viewingGallery ? (
        <ImageViewerModal
          images={viewingGallery.images}
          initialIndex={viewingGallery.index}
          onClose={() => setViewingGallery(null)}
          onDownload={handleDownloadSource}
        />
      ) : null}
      {viewingLinkedTask ? (
        <TaskViewDialog
          task={linkedTasks.find((itemTask) => itemTask.id === viewingLinkedTask.id) || viewingLinkedTask}
          onClose={() => setViewingLinkedTask(null)}
          usePortal
          zIndexClass="z-[1100]"
          onDelegated={(updated) => {
            if (!updated?.id) return;
            setFetchedTasks((prev) => uniqueTasksById(prev, updated));
            setViewingLinkedTask((prev) => (
              prev?.id === updated.id ? { ...prev, ...updated } : prev
            ));
          }}
          onNavigateToMessage={(navTask) => {
            setViewingLinkedTask(null);
            window.dispatchEvent(new CustomEvent('workloop:go-to-message', {
              detail: {
                messageId: navTask?.messageId,
                channelId: navTask?.channelId || item?.channelId,
                replyToMessageId: navTask?.replyToMessageId || null,
              },
            }));
          }}
          onOpenWorkflow={(opened) => {
              if (viewingLinkedTask?.id) {
                setFetchedTasks((prev) => uniqueTasksById(viewingLinkedTask, prev));
              }
              setViewingLinkedTask(null);
              if (opened?.messageId && opened.messageId !== item?.messageId) {
                window.dispatchEvent(new CustomEvent('workloop:open-workflow', { detail: opened }));
              }
            }}
        />
      ) : null}
      <VoiceNoteSendModal
        isOpen={Boolean(commentVoice.review)}
        caption={commentVoice.review?.caption || ''}
        captioning={commentVoice.captioning}
        onCaptionChange={commentVoice.updateReviewCaption}
        audioUrl={commentVoice.review?.url}
        durationSeconds={commentVoice.review?.durationSeconds}
        sending={sendingComment}
        speechSupported={commentVoice.speechSupported}
        speechHint={commentVoice.speechHint}
        zIndexClass="z-[2200]"
        onSendWithAudio={(caption) => sendCommentVoice({ includeAudio: true, includeCaption: true, caption })}
        onSendAudioOnly={() => sendCommentVoice({ includeAudio: true, includeCaption: false })}
        onSendCaptionOnly={(caption) => sendCommentVoice({ includeAudio: false, includeCaption: true, caption })}
        onDiscard={() => commentVoice.discardReview()}
      />
      <ConfirmModal
        isOpen={forceCloseConfirmOpen}
        type="danger"
        title="Force close workflow"
        message="Force close this workflow? Every linked task and subtask will be marked complete, then the workflow will be closed."
        confirmText="Force close"
        cancelText="Cancel"
        onClose={() => setForceCloseConfirmOpen(false)}
        onConfirm={confirmForceClose}
      />
    </>
  );
};

export default WorkflowViewDialog;
